/**
 * Het vangnet onder een lopende opname.
 *
 * Elke tien seconden komt er een stuk binnen, en dat gaat hier meteen naar
 * `IndexedDB`. Klapt het tabblad dicht of loopt de browser vast, dan staan de
 * stukken er bij de volgende keer openen nog en biedt het scherm de opname
 * alsnog aan.
 *
 * Bewust niet tijdens de opname naar de server: dat vraagt een plek voor losse
 * stukken, een stap om ze samen te voegen en een opruiming voor gesprekken die
 * nooit zijn afgemaakt. Dit dekt hetzelfde risico zonder dat de server iets
 * nieuws hoeft te kennen.
 *
 * Alles hier faalt zacht. Een browser in privémodus of zonder ruimte geeft geen
 * opslag, en dan gaat de opname gewoon door: het vangnet mag nooit de opname
 * zelf zijn.
 */

const DB_NAAM = 'vip-opnames';
const VERSIE = 1;
/** Eén rij per opname: wat het is en wanneer hij begon. */
const OPNAMES = 'opnames';
/** De stukken zelf, met `[opnameId, nummer]` als sleutel zodat ze in volgorde
 *  terugkomen — aan elkaar geplakt in díe volgorde is het pas afspeelbaar. */
const STUKKEN = 'stukken';

export interface OpnameRij {
  id: string;
  type: string;
  begonnenOp: string;
}

export interface OpnameStukken extends OpnameRij {
  blob: Blob;
}

function open(): Promise<IDBDatabase> {
  return new Promise((klaar, mislukt) => {
    const verzoek = indexedDB.open(DB_NAAM, VERSIE);
    verzoek.onupgradeneeded = () => {
      const db = verzoek.result;
      if (!db.objectStoreNames.contains(OPNAMES)) db.createObjectStore(OPNAMES, { keyPath: 'id' });
      if (!db.objectStoreNames.contains(STUKKEN)) db.createObjectStore(STUKKEN);
    };
    verzoek.onsuccess = () => klaar(verzoek.result);
    verzoek.onerror = () => mislukt(verzoek.error ?? new Error('Opslag niet beschikbaar'));
  });
}

function klaarMet(tx: IDBTransaction): Promise<void> {
  return new Promise((klaar, mislukt) => {
    tx.oncomplete = () => klaar();
    tx.onerror = () => mislukt(tx.error ?? new Error('Opslag mislukt'));
    tx.onabort = () => mislukt(tx.error ?? new Error('Opslag afgebroken'));
  });
}

const alles = <T>(verzoek: IDBRequest<T>): Promise<T> =>
  new Promise((klaar, mislukt) => {
    verzoek.onsuccess = () => klaar(verzoek.result);
    verzoek.onerror = () => mislukt(verzoek.error ?? new Error('Lezen mislukt'));
  });

export async function startOpslag(rij: Omit<OpnameRij, 'id'>): Promise<string> {
  const id = crypto.randomUUID();
  try {
    const db = await open();
    const tx = db.transaction(OPNAMES, 'readwrite');
    tx.objectStore(OPNAMES).put({ id, ...rij });
    await klaarMet(tx);
    db.close();
  } catch (err) {
    // Geen opslag is geen reden om niet op te nemen; het id blijft geldig en
    // de stukken verdwijnen dan gewoon in het niets.
    console.warn('[opname] geen opslag beschikbaar', err);
  }
  return id;
}

export async function bewaarStuk(opnameId: string, nummer: number, stuk: Blob): Promise<void> {
  const db = await open();
  const tx = db.transaction(STUKKEN, 'readwrite');
  tx.objectStore(STUKKEN).put(stuk, sleutel(opnameId, nummer));
  await klaarMet(tx);
  db.close();
}

/** Het nummer met nullen ervoor, zodat de sleutels op volgorde blijven staan:
 *  zonder dat komt stuk 10 vóór stuk 2 en is de opname onhoorbaar. */
const sleutel = (opnameId: string, nummer: number) =>
  `${opnameId}#${String(nummer).padStart(6, '0')}`;

/** Klaar met deze opname: de stukken zijn samengevoegd tot een bestand, dus wat
 *  hier ligt is dubbel. */
export async function sluitOpnameAf(opnameId: string): Promise<void> {
  const db = await open();
  const tx = db.transaction([OPNAMES, STUKKEN], 'readwrite');
  tx.objectStore(OPNAMES).delete(opnameId);
  const bereik = IDBKeyRange.bound(`${opnameId}#`, `${opnameId}#￿`);
  tx.objectStore(STUKKEN).delete(bereik);
  await klaarMet(tx);
  db.close();
}

/**
 * De laatste opname die niet netjes is afgesloten, aan elkaar geplakt.
 *
 * Levert niets op als er geen stukken bij staan: een opname waarvan alleen de
 * rij bestaat is een start die meteen misging, en die aanbieden zou een leeg
 * bestand opleveren.
 *
 * Geeft een `Blob` en geen `File`: hoe een opname heet is een vraag van
 * `opname.ts`, en die kant op verwijzen zou de twee bestanden aan elkaar
 * knopen.
 */
export async function vindOnafgemaakteOpname(): Promise<OpnameStukken | null> {
  try {
    const db = await open();
    const rijen = await alles<OpnameRij[]>(
      db.transaction(OPNAMES, 'readonly').objectStore(OPNAMES).getAll(),
    );
    const rij = rijen.sort((a, b) => b.begonnenOp.localeCompare(a.begonnenOp))[0];
    if (!rij) {
      db.close();
      return null;
    }

    const bereik = IDBKeyRange.bound(`${rij.id}#`, `${rij.id}#￿`);
    const stukken = await alles<Blob[]>(
      db.transaction(STUKKEN, 'readonly').objectStore(STUKKEN).getAll(bereik),
    );
    db.close();
    if (stukken.length === 0) return null;

    return { ...rij, blob: new Blob(stukken, { type: rij.type }) };
  } catch (err) {
    console.warn('[opname] herstel niet mogelijk', err);
    return null;
  }
}

/** Weggooien wat er van een afgebroken opname over is. */
export const vergeetOpname = (opnameId: string) => sluitOpnameAf(opnameId).catch(() => {});
